import { PRINT_FONT_LABELS, type PrintFont, printFontSchema, type PrintTheme } from '#shared/types';
import { Select } from '#client/components/ui/Select';
import { ColorField } from '../controls/ColorField';
import { NumberField } from '../controls/NumberField';

interface ThemeSectionProps {
  theme: PrintTheme;
  onChange: (patch: Partial<PrintTheme>) => void;
}

/**
 * Typeface, size and the four colours the sheet is drawn in.
 *
 * Only three typefaces, and deliberately so: these are the PDF standard fonts,
 * present in every reader and every PostScript printer. They embed no font data
 * and cannot be substituted on someone else's machine — which on a clinical
 * document is worth more than a longer list.
 */
export function ThemeSection({ theme, onChange }: ThemeSectionProps) {
  return (
    <>
      <Select
        label="Typeface"
        value={theme.font}
        onChange={(choice) => onChange({ font: choice })}
        options={printFontSchema.options.map((font: PrintFont) => ({
          value: font,
          label: PRINT_FONT_LABELS[font],
        }))}
        helpText="All three are built into every PDF reader, so nothing is embedded and nothing is substituted."
      />

      <NumberField
        label="Base type size"
        unit="pt"
        value={theme.base_size}
        onChange={(base_size) => onChange({ base_size })}
        min={5}
        max={16}
        step={0.5}
        helpText="Headings, the letterhead and the footer are sized relative to this."
      />

      <div className="c-builder__grid">
        <ColorField
          label="Text"
          value={theme.text_color}
          onChange={(text_color) => onChange({ text_color })}
        />
        <ColorField
          label="Secondary text"
          value={theme.muted_color}
          onChange={(muted_color) => onChange({ muted_color })}
        />
        <ColorField
          label="Accent"
          value={theme.accent_color}
          onChange={(accent_color) => onChange({ accent_color })}
          helpText="Headings and the long part of the rule."
        />
        <ColorField
          label="Second accent"
          value={theme.accent_alt_color}
          onChange={(accent_alt_color) => onChange({ accent_alt_color })}
          helpText="The short leading segment of the rule."
        />
      </div>
    </>
  );
}
